import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faTruckFast } from '@fortawesome/free-solid-svg-icons';

import StockModal from './StockModal';

/**
 * "We are running low on this."
 *
 * ONE QUESTION, TWO ANSWERS. The person raising this is standing at a shelf
 * with a trolley in one hand. They are not being asked how many to buy — that
 * needs the pack size and the price, which the manager has and they do not
 * (stockService.validQuantity leaves quantity null until the purchase is made)
 * — and they are not being asked to write a note. They tap the item, they say
 * how soon, they are done.
 *
 * The same dialog changes an existing request's urgency: it is the same single
 * question, so it is the same single screen.
 */
export default function RequestModal({ item, request, onClose, onSubmit }) {
    const editing = !!request;
    const name = item?.name || request?.item_name;
    const eta = item?.delivery_estimate || request?.item_delivery_estimate;

    const [urgency, setUrgency] = useState(request?.urgency || 'normal');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const send = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        setError('');
        try {
            await onSubmit({
                itemId: item?.id || request?.item_id,
                requestId: request?.id,
                urgency,
            });
        } catch (err) {
            setError(err.response?.data?.error || 'That did not send. Try again.');
            setSaving(false);
        }
    };

    return (
        <StockModal
            title={editing ? `Change — ${name}` : `Request ${name}`}
            subtitle={editing
                ? 'How soon is it needed?'
                : 'The managers are told the moment you send it.'}
            onClose={onClose}
            width={500}
        >
            <div className="stk__pickedItem">
                <strong>{name}</strong>
                {eta ? (
                    <span><FontAwesomeIcon icon={faTruckFast} /> Delivery usually {eta}</span>
                ) : null}
            </div>

            {/* PICK, THEN SEND. Sending on the tap that picks means a mis-tap is
                already a request somebody has to chase; two side-by-side choices
                and one Send is still two taps, and the second one is a decision. */}
            <form onSubmit={send}>
                <span className="stk__fieldLabel">How soon do you need it?</span>
                <div className="stk__choices stk__choices--pair">
                    <button
                        type="button"
                        className={`stk__choice ${urgency === 'normal' ? 'is-active' : ''}`}
                        onClick={() => setUrgency('normal')}
                    >
                        <strong>Running low</strong>
                        <span>Still enough for now — order it on the next run.</span>
                    </button>
                    <button
                        type="button"
                        className={`stk__choice stk__choice--urgent ${urgency === 'urgent' ? 'is-active' : ''}`}
                        onClick={() => setUrgency('urgent')}
                    >
                        <strong><FontAwesomeIcon icon={faBolt} /> Urgent</strong>
                        <span>Finished, or gone within a day. Goes to the top.</span>
                    </button>
                </div>

                {error ? <p className="stk__formError">{error}</p> : null}

                <div className="stk__modalActions">
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="stk__btn stk__btn--primary" disabled={saving}>
                        {saving ? 'Sending…' : (editing ? 'Save' : 'Send request')}
                    </button>
                </div>
            </form>
        </StockModal>
    );
}
